import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { faFacebookF, faLinkedin, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import MenuIcon from '../MenuIcon';
import { ExternalLink, IconArrowHead, Logo, NamedLink } from '../../../../components';
import { LISTING_CATEGORY } from '../../../../constants/listing.constants';

import css from './GeneralMenu.module.css';

const GENERAL_MENU_LINKS = [
  {
    label: <FormattedMessage id="GeneralMenu.placeYourAd" />,
    key: 'placeYourAd',
    name: 'SignupForUserTypePage',
    params: { userType: 'privateSeller' },
  },
  {
    label: <FormattedMessage id="GeneralMenu.searchForBoats" />,
    key: 'searchForBoats',
    name: 'LandingPage',
  },
  {
    label: <FormattedMessage id="GeneralMenu.home" />,
    key: 'home',
    name: 'LandingPage',
  },
  {
    label: <FormattedMessage id="GeneralMenu.boatingNews" />,
    href: 'https://www.nauticlink.com/nieuws-en-opinie/',
  },
  {
    label: <FormattedMessage id="GeneralMenu.boatingGuide" />,
    key: 'boatingGuide',
    sublinks: [
      {
        href: 'https://www.nauticlink.com/bootnamen/',
        label: <FormattedMessage id="GeneralMenu.boatNames" />,
      },
      {
        href: 'https://www.nauticlink.com/evenementen/',
        label: <FormattedMessage id="GeneralMenu.eventsCalendar" />,
      },
      {
        key: 'weather',
        label: <FormattedMessage id="GeneralMenu.weatherAndNavigation" />,
        sublinks: [
          {
            href: 'https://www.nauticlink.com/windverwachting/',
            label: <FormattedMessage id="GeneralMenu.windForecast" />,
          },
          {
            href: 'https://www.nauticlink.com/buienradar/',
            label: <FormattedMessage id="GeneralMenu.rainRadar" />,
          },

          {
            href: 'https://www.nauticlink.com/navtex-weerbericht/',
            label: <FormattedMessage id="GeneralMenu.navtexWeatherForecast" />,
          },
          {
            href: 'https://www.nauticlink.com/weerkaarten/',
            label: <FormattedMessage id="GeneralMenu.weatherMaps" />,
          },
          {
            href: 'https://www.nauticlink.com/waterkaart-nederland-inclusief-ais/',
            label: <FormattedMessage id="GeneralMenu.waterMapNetherlandsIncludingAIS" />,
          },
          {
            href: 'https://www.nauticlink.com/waterstanden-nap/',
            label: <FormattedMessage id="GeneralMenu.waterLevelsNAP" />,
          },
          {
            href: 'https://www.nauticlink.com/getijden/',
            label: <FormattedMessage id="GeneralMenu.tides" />,
          },
        ],
      },
    ],
  },
  {
    label: <FormattedMessage id="GeneralMenu.sailingParnters" />,
    key: 'sailingPartners',
    href: 'https://www.nauticlink.com/vaarpartners/',
  },
  {
    href: 'https://www.nauticlink.com/contact/',
    label: <FormattedMessage id="GeneralMenu.contact" />,
  },
];

const renderLink = linkItem => {
  const { key, href, label, name, to, sublinks, active, params } = linkItem;

  const isSummary = !!sublinks && sublinks.length > 0;
  const isNamedLink = !!name;
  const isExternalLink = !!href;

  const DetailsWrapper = ({ children }) =>
    isSummary ? <details>{children}</details> : <>{children}</>;
  const SummaryWrapper = ({ children }) =>
    isSummary ? <summary className={css.summary}>{children}</summary> : <>{children}</>;

  const SummaryBody = () =>
    !isSummary ? null : <ul className={css.sublinks}>{sublinks.map(renderLink)}</ul>;

  const LinkWrapper = ({ children }) =>
    isNamedLink ? (
      <NamedLink
        name={name}
        to={to}
        params={params}
        className={classNames(css.link, { [css.linkActive]: active })}
      >
        {children}
      </NamedLink>
    ) : isExternalLink ? (
      <ExternalLink href={href} className={css.link} target="_self">
        {children}
      </ExternalLink>
    ) : (
      <span className={css.link}>{children}</span>
    );

  return (
    <li key={key ?? href ?? (to?.search ? `${name}${to.search}` : name)} className={css.listItem}>
      <DetailsWrapper>
        <SummaryWrapper>
          <LinkWrapper>{label}</LinkWrapper>
        </SummaryWrapper>
        <SummaryBody />
      </DetailsWrapper>
    </li>
  );
};

const GeneralMenu = props => {
  const { className, onManageDisableScrolling } = props;

  const [isOpen, setIsOpen] = useState(null);

  const menuRef = useRef(null);

  const handleOpen = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      onManageDisableScrolling('generalMenu', true);
    }
  }, [isOpen, onManageDisableScrolling]);

  const handleClose = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      onManageDisableScrolling('generalMenu', false);
    }
  }, [isOpen, onManageDisableScrolling]);

  useEffect(() => {
    const closeMenuOnClickOutside = e => {
      if (isOpen && !menuRef.current?.contains(e.target)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', closeMenuOnClickOutside);
    return () => {
      document.removeEventListener('mousedown', closeMenuOnClickOutside);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) {
      onManageDisableScrolling('generalMenu', false);
    }
  }, [isOpen]);

  return (
    <>
      <div onClick={handleOpen} className={className}>
        <MenuIcon className={css.hamburgerMenuIcon} />
      </div>
      <div
        ref={menuRef}
        className={classNames(css.menu, {
          [css.menuOpen]: isOpen,
          [css.menuClosed]: isOpen === false,
        })}
      >
        <div className={css.menuHeader}>
          <NamedLink className={css.logoWrapper} name="LandingPage">
            <Logo alt="Menu logo" layout="desktop" />
          </NamedLink>
          <span className={css.iconClickWrapper} onClick={handleClose}>
            <IconArrowHead direction="right" className={css.icon} />
          </span>
        </div>
        <nav className={classNames(css.menuBody, css.scrollLayer)}>
          <ul className={css.links}>{GENERAL_MENU_LINKS.map(renderLink)}</ul>
          <div className={css.separator} />
          <div className={css.socials}>
            <ExternalLink href="mailto:office@nauticlink.com">
              <FontAwesomeIcon className={css.social} icon={faEnvelope} />
            </ExternalLink>
            <ExternalLink href="https://www.facebook.com/nauticlink/">
              <FontAwesomeIcon className={css.social} icon={faFacebookF} />
            </ExternalLink>
            {/*<ExternalLink href="https://twitter.com/nauticlink">
              <FontAwesomeIcon className={css.social} icon={faXTwitter} />
            </ExternalLink>
            <ExternalLink href="https://www.linkedin.com/company/nauticlink/">
              <FontAwesomeIcon className={css.social} icon={faLinkedin} />
            </ExternalLink>*/}
          </div>
        </nav>
      </div>
    </>
  );
};

export default GeneralMenu;
